import { type EmbedAction, EmbedActionSchema } from '../../shared/embed'

/**
 * Embed Bridge Infrastructure Service
 * Handles communication between the embedded app and parent window
 */
export type EmbedActionHandler = (action: EmbedAction) => void | Promise<void>

export type EmbedBridge = {
  onAction: (handler: EmbedActionHandler) => () => void
  notifyReady: () => void
  isEmbedded: () => boolean
}

/**
 * Creates an embed bridge instance
 * Uses closure to maintain private state
 */
export const createEmbedBridge = (): EmbedBridge => {
  // Private state
  const isEmbeddedMode = window.parent !== window
  const handlers = new Set<EmbedActionHandler>()

  // Private helper to handle incoming actions
  const handleAction = async (action: unknown) => {
    try {
      const validatedAction = EmbedActionSchema.parse(action)

      // Execute all handlers
      await Promise.all(Array.from(handlers).map((handler) => handler(validatedAction)))
    } catch (error) {
      console.error('[EmbedBridge] Invalid action received:', error)
    }
  }

  // Set up listeners immediately
  ;(() => {
    // Listen to postMessage for production embed
    window.addEventListener('message', (event) => {
      if (event.data?.type === 'design-studio-action' && event.data.action) {
        handleAction(event.data.action)
      }
    })

    // Listen to CustomEvents for testing
    window.addEventListener('design-studio-action', ((event: CustomEvent) => {
      if (event.detail) {
        handleAction(event.detail)
      }
    }) as EventListener)
  })()

  // Public API
  return {
    onAction: (handler: EmbedActionHandler) => {
      handlers.add(handler)

      // Return unsubscribe function
      return () => {
        handlers.delete(handler)
      }
    },

    notifyReady: () => {
      console.warn('[EmbedBridge] Notifying parent that Design Studio is ready')

      if (isEmbeddedMode) {
        window.parent.postMessage({ type: 'design-studio-ready' }, '*')
      }

      // Also dispatch CustomEvent for same-window testing
      window.dispatchEvent(new CustomEvent('design-studio-ready'))
    },

    isEmbedded: () => isEmbeddedMode,
  }
}

// Export singleton instance
export const embedBridge = createEmbedBridge()
